import React, { useMemo, useState, useEffect } from 'react';
import { useAtom } from 'jotai';
import useTranslate from '../../Hooks/useTranslate';
import {
  Box,
  Button,
  IconButton,
  Typography,
  Paper,
  TextField,
  Collapse,
} from '@mui/material';
import { CenteredFlexBox } from '../../Components/FlexBox';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import AddIcon from '@mui/icons-material/Add';
import { makeGenericFieldAtom } from '../../store/genericFormStore';
import { DynamicSelect } from './DynamicSelect';
import { getFullPath } from './BasicField';
import { SchemaRenderer } from './SchemaRenderer';

export const ArrayField = ({ basePath, field }) => {
  const translate = useTranslate();
  const path = getFullPath(basePath, field.key);

  const fieldAtom = useMemo(
    () => makeGenericFieldAtom(path, field.defaultValue || []),
    [path]
  );
  const [items, setItems] = useAtom(fieldAtom);
  const [collapsed, setCollapsed] = useState(false);

  // INIZIALIZZAZIONE SILENZIOSA: Inietta i default nello store globale al mount
  // senza scatenare "formChanged = true"
  useEffect(() => {
    const expected = JSON.stringify(field.defaultValue || []);
    const current = JSON.stringify(items || []);

    if (expected !== current) {
      setItems(field.defaultValue || [], { isInit: true });
    }
  }, [field.defaultValue, items, setItems]);

  const toggleCollapsed = () => setCollapsed((prev) => !prev);

  const handleAddItem = () => {
    const newItem = field.itemSchema.reduce((acc, subField) => {
      acc[subField.key] = subField.defaultValue ?? '';
      return acc;
    }, {});

    // Essendo un'azione dell'utente, qui NON usiamo { isInit: true }
    // così il formChanged scatta regolarmente.
    setItems([...(Array.isArray(items) ? items : []), newItem]);
  };

  const handleRemoveItem = (indexToRemove) => {
    setItems((items || []).filter((_, index) => index !== indexToRemove));
  };

  const handleSubFieldChange = (index, subKey, newValue) => {
    const updatedItems = [...(Array.isArray(items) ? items : [])];
    updatedItems[index] = { ...updatedItems[index], [subKey]: newValue };
    setItems(updatedItems);
  };

  return (
    <Paper variant="outlined" sx={{ p: '16px' }}>
      <CenteredFlexBox justifyContent="space-between">
        <Typography variant="h6">{translate(field.label)}</Typography>
        <IconButton id="toggle-paper" onClick={toggleCollapsed}>
          <KeyboardArrowDownRoundedIcon
            sx={{
              transform: !collapsed ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.3s ease',
            }}
          />
        </IconButton>
      </CenteredFlexBox>
      <Collapse in={!collapsed}>
        {(Array.isArray(items) ? items : []).map((item, index) => (
          <Paper
            key={index}
            variant="outlined"
            sx={{
              p: 2,
              mb: 1,
              display: 'flex',
              gap: 2,
              alignItems: 'center',
            }}
          >
            <SchemaRenderer
              fields={field.itemSchema}
              basePath={`${path}.${index}`}
            />
            <IconButton onClick={() => handleRemoveItem(index)} size="small">
              <DeleteOutlinedIcon />
            </IconButton>
          </Paper>
        ))}
        <Button
          startIcon={<AddIcon />}
          onClick={handleAddItem}
          size="small"
          variant="text"
        >
          {translate('ADD_ITEM')}
        </Button>
      </Collapse>
    </Paper>
  );
};
